/**
 * Hierarchy Page
 * Visual representation of role hierarchy and user reporting structure
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip,
  IconButton,
} from '@mui/material';
import { ExpandMore, ChevronRight, Person, Group } from '@mui/icons-material';
import { useUserManagement } from '@/hooks/useUserManagement';
import type { Role, User } from '@/services/user-management.service';
import { roleLabel } from '@/utils/roleLabel.util';

const Hierarchy: React.FC = () => {
  const {
    roleHierarchy,
    users,
    loading,
    error,
    loadRoleHierarchy,
    loadUsers,
    getUserReportingTree,
  } = useUserManagement();

  const [tabValue, setTabValue] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [reportingTree, setReportingTree] = useState<any>(null);
  const [loadingTree, setLoadingTree] = useState(false);

  useEffect(() => {
    loadRoleHierarchy();
    loadUsers();
  }, [loadRoleHierarchy, loadUsers]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLoadReportingTree = async () => {
    if (!selectedUserId) return;

    try {
      setLoadingTree(true);
      const tree = await getUserReportingTree(selectedUserId);
      setReportingTree(tree);
    } catch (err) {
      // Error handled by hook
    } finally {
      setLoadingTree(false);
    }
  };

  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());

  const toggleRole = (roleId: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
    }
    setExpandedRoles(newExpanded);
  };

  const renderRoleTree = (roles: Role[], level = 0): React.ReactNode => {
    return (
      <List component="div" disablePadding>
        {roles.map((role) => {
          const hasChildren = role.children && role.children.length > 0;
          const isExpanded = expandedRoles.has(role._id);

          return (
            <Box key={role._id}>
              <ListItem
                sx={{ pl: level * 4 }}
                secondaryAction={
                  hasChildren && (
                    <IconButton edge="end" onClick={() => toggleRole(role._id)}>
                      {isExpanded ? <ExpandMore /> : <ChevronRight />}
                    </IconButton>
                  )
                }
              >
                <Group fontSize="small" sx={{ mr: 1 }} />
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {roleLabel(role.name)}
                      </Typography>
                      <Chip label={role.key} size="small" />
                      {role.permissions && role.permissions.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({role.permissions.length} permissions)
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={`Level ${role.level}`}
                />
              </ListItem>
              {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {renderRoleTree(role.children || [], level + 1)}
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    );
  };

  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const toggleUser = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const renderUserTree = (user: User, reports: User[], level = 0): React.ReactNode => {
    const hasReports = reports.length > 0;
    const isExpanded = expandedUsers.has(user._id);

    return (
      <Box>
        <ListItem
          sx={{ pl: level * 4 }}
          secondaryAction={
            hasReports && (
              <IconButton edge="end" onClick={() => toggleUser(user._id)}>
                {isExpanded ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
            )
          }
        >
          <Person fontSize="small" sx={{ mr: 1 }} />
          <ListItemText
            primary={`${user.firstName} ${user.lastName}`}
            secondary={`${user.email} - ${roleLabel(user.role)}`}
          />
        </ListItem>
        {hasReports && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {reports.map((report) => {
                const reportReports = users.filter((u) => u.reportingTo?._id === report._id);
                return (
                  <Box key={report._id}>{renderUserTree(report, reportReports, level + 1)}</Box>
                );
              })}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Role Hierarchy" />
        <Tab label="User Reporting Tree" />
      </Tabs>

      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Role Hierarchy
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : roleHierarchy.length === 0 ? (
              <Typography align="center" color="text.secondary" p={3}>
                No roles found. Create roles to see hierarchy.
              </Typography>
            ) : (
              renderRoleTree(roleHierarchy)
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              User Reporting Tree
            </Typography>
            <Box display="flex" gap={2} mb={3}>
              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Select User</InputLabel>
                <Select
                  value={selectedUserId}
                  label="Select User"
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleLoadReportingTree}
                disabled={!selectedUserId || loadingTree}
              >
                {loadingTree ? <CircularProgress size={20} /> : 'Load Tree'}
              </Button>
            </Box>

            {loadingTree ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : reportingTree ? (
              <Box>
                <Typography variant="subtitle1" mb={2}>
                  User: {reportingTree.user.firstName} {reportingTree.user.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Total Reports: {reportingTree.totalReports}
                </Typography>
                {reportingTree.directReports.length > 0 ? (
                  <List>{renderUserTree(reportingTree.user, reportingTree.directReports)}</List>
                ) : (
                  <Typography color="text.secondary">This user has no direct reports.</Typography>
                )}
              </Box>
            ) : (
              <Typography align="center" color="text.secondary" p={3}>
                Select a user to view their reporting tree.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Hierarchy;
